import { Box, MenuList, MenuItem, Menu, MenuButton, IconButton } from "@chakra-ui/react";
import NoResults from "components/elements/NoResults";
import pipeLineToolTip from "pages/Analytics/elements/AmountStatusBar/pipelineTooltip";
import { LoansContext } from "providers/loans";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useContext, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import html2canvas from "html2canvas";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  LabelList,
  Cell,
  Tooltip,
} from "recharts";
import colors from "utils/colors";
import { useNavigate } from "react-router-dom";

const LoanPipeline = () => {
  const { loans, setFilters, filters } = useContext(LoansContext);
  const { user } = useTenant();
  const navigate = useNavigate();
  const [chartDataNew, setChartData] = useState<{ name: string; value: number }[]>([]);
  //const { loan } = useContext(LoansContext);
  const widgetBoxRef = useRef<HTMLDivElement>(null);

  const barColors = [
    "#9E9AA3",
    "#45C14A",
    "#F2B821",
    "#00A896",
    "#FF2020",
    "#959F22",
    "#EC7211",
    "#1CA1EC",
    "#455DD9",
    "#A166FF",
  ];

  const handleCellClick = (entry: any) => {
    if (entry.value > 0) {
      const status = user.company.statuses.find((status: any) => status.label === entry.name);
      const statusValues = {
        label: status.label,
        value: status.id,
      };
      setFilters({
        ...filters,
        status: [statusValues],
      });
      navigate(`/loans`);
    }
  };

  useEffect(() => {
    setChartData(
      user.company.statuses
        .map((status: any) => {
          const filteredLoans = loans.filter((loan: any) => loan.status.id === status.id);
          const loanAmounts = filteredLoans.map((loan: any) => loan.amount);
          const loanName = filteredLoans.map((loan: any) => loan.name);

          return {
            name: status.label,
            value: filteredLoans.length,
            loanAmounts: loanAmounts,
            loanName: loanName,
            order: status.order,
          };
        })
        .sort((a: any, b: any) => a.order - b.order)
    );
  }, [loans, user.company.statuses]);

  const handleExport = () => {
    if (widgetBoxRef.current) {
      const exportElement = document.getElementById("hidePipelineDropdown");
      if (exportElement) {
        exportElement.style.display = "none";
      }
      html2canvas(widgetBoxRef.current, { scale: 2 }).then(canvas => {
        const imageURL = canvas.toDataURL();
        const link = document.createElement("a");
        link.href = imageURL;
        link.download = "return_box_image.png";
        link.click();
        if (exportElement) {
          exportElement.style.display = "block";
        }
      });
    }
  };

  useEffect(() => {
    setFilters({});
  }, [setFilters]);

  return chartDataNew.length > 0 ? (
    <Box ref={widgetBoxRef} overflow={"auto"}>
      <Box minWidth="1300px" width="100%">
        <ResponsiveContainer height={300}>
          <BarChart
            data={chartDataNew}
            barSize={16}
            margin={{
              top: 26,
              left: 0,
              bottom: 10,
              right: 40,
            }}
          >
            <CartesianGrid vertical={false} stroke="#F5F5F5" />
            <XAxis
              dataKey="name"
              type="category"
              axisLine={false}
              tickLine={false}
              interval={0}
              height={55}
              tick={(props: any) => {
                const { x, y, payload } = props;
                const words = payload.value.split(" ");
                const topMargin = 10;
                return (
                  <g transform={`translate(${x},${y + topMargin})`}>
                    <text fontSize="10" textAnchor="middle" fill="#666">
                      {words.map((word: string, index: number) => (
                        <tspan x="0" dy={index === 0 ? 0 : 12} key={index}>
                          {word}
                        </tspan>
                      ))}
                    </text>
                  </g>
                );
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tickCount={6}
              width={24}
              tick={{
                fontSize: "10px",
                color: colors.GRAY_300,
              }}
            />
            <Tooltip content={props => pipeLineToolTip(props, { user: user })} />
            <Bar dataKey="value" opacity={4} radius={[15, 15, 0, 0]} label={false}>
              {chartDataNew.map((entry, index) => (
                <Cell
                  key={`chart-data-new-${index}`}
                  fill={barColors[index % barColors.length]}
                  onClick={() => handleCellClick(entry)}
                />
              ))}
              <LabelList
                dataKey="value"
                fontSize="16px"
                fontWeight="700"
                fill="#717579"
                position="top"
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <Box position={"absolute"} top={"0"} right={"0"} id="hidePipelineDropdown" mr={"9px"}>
          <Menu>
            <MenuButton
              as={IconButton}
              background="transparent"
              color="gray.200"
              borderWidth="0px"
              _hover={{
                background: "transparent",
                color: "primary",
              }}
              aria-label="document-actions"
              icon={<FontAwesomeIcon icon={["far", "ellipsis-vertical"]} />}
            />
            <MenuList position={"absolute"} right={"10%"}>
              <MenuItem onClick={handleExport}>Export</MenuItem>
            </MenuList>
          </Menu>
        </Box>
      </Box>
    </Box>
  ) : (
    <NoResults message="Not enough data yet..." />
  );
};

export default LoanPipeline;
